import React from 'react';
import { Spinner } from 'react-bootstrap';

import Tag from 'components/DesignSystem/Tag';
import { formatDate } from 'components/Admin/Products/Line';
import ContractAttachments from 'components/Admin/Companies/Table/Row/Details/Attachments';
import { CONSULTANCY, METEREOLOGY } from 'constants/contractType';
import { ListedCompanyContract } from 'helper/companies';
import { formattedContractValue } from 'helper/contracts';
import { availabilityTagTypeMap } from 'helper/products';
import { ResponseError } from 'services/api/base';
import { useGetCompanyQuery } from 'services/api/companies';
import { useGetContractQuery, useGetReadjustmentRatesQuery } from 'services/api/contracts';
import useHandleRequestErrors from 'utils/hooks/useHandleRequestErrors';

import './index.scss';

const CONTRACT_TYPE_LABELS: Record<number, string> = {
  [METEREOLOGY]: 'Ampere Meteorologia',
  [CONSULTANCY]: 'Ampere Consultoria',
};

export default function Details({ company }: { company: ListedCompanyContract }) {
  const {
    data: companyData,
    error,
    isLoading,
  } = useGetCompanyQuery({ companyId: company.empresa_id });
  const {
    data: contractData,
    error: contractError,
    isLoading: contractLoading,
  } = useGetContractQuery({ contractId: company.contrato_id });
  const {
    data: readjustmentRateData,
    isLoading: readjustmentDataIsLoading,
  } = useGetReadjustmentRatesQuery();

  useHandleRequestErrors(error as ResponseError);
  useHandleRequestErrors(contractError as ResponseError);

  if (isLoading || contractLoading) {
    return (
      <div className="companies-mobile-details-loading">
        <Spinner animation="border" size="sm" />
        <span>Carregando detalhes…</span>
      </div>
    );
  }

  const readjustmentRate = readjustmentRateData?.find(
    ({ id }) => id === contractData?.readjustmentIndex,
  );
  const readjustmentRateLabel = readjustmentRate?.label || 'Não informado';
  const contractTypeLabel = contractData === undefined
    ? 'Não informado'
    : CONTRACT_TYPE_LABELS[contractData.type] || 'Não informado';
  const users = companyData?.users || [];
  const productsCount = company.products.length;
  const address = [companyData?.endereco, companyData?.numero, companyData?.complemento]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="companies-mobile-details">
      <p className="companies-mobile-details-title">Informações gerais</p>
      <div className="companies-mobile-details-rows">
        <div className="companies-mobile-details-row">
          <span>Nome fantasia</span>
          <span>{companyData?.nome_fantasia || 'Não informado'}</span>
        </div>
        <div className="companies-mobile-details-row">
          <span>Apelido</span>
          <span>{companyData?.apelido || 'Não informado'}</span>
        </div>
        <div className="companies-mobile-details-row">
          <span>CNPJ</span>
          <span>{companyData?.cnpj || 'Não informado'}</span>
        </div>
        {companyData?.ie && (
          <div className="companies-mobile-details-row">
            <span>Inscrição estadual</span>
            <span>{companyData.ie}</span>
          </div>
        )}
        <div className="companies-mobile-details-row">
          <span>E-mails</span>
          <span className="companies-mobile-details-emails">
            {companyData?.emails_fiscais || 'Não informado'}
          </span>
        </div>
      </div>

      <p className="companies-mobile-details-title">Endereço</p>
      <div className="companies-mobile-details-rows">
        <div className="companies-mobile-details-row">
          <span>Logradouro</span>
          <span>{address || 'Não informado'}</span>
        </div>
        <div className="companies-mobile-details-row">
          <span>Bairro</span>
          <span>{companyData?.bairro || 'Não informado'}</span>
        </div>
        <div className="companies-mobile-details-row">
          <span>Cidade / UF</span>
          <span>
            {companyData?.municipio?.nome
              ? `${companyData.municipio.nome} / ${companyData?.estado?.uf || '-'}`
              : 'Não informado'}
          </span>
        </div>
        <div className="companies-mobile-details-row">
          <span>CEP</span>
          <span>{companyData?.cep || 'Não informado'}</span>
        </div>
      </div>

      <p className="companies-mobile-details-title">Contrato</p>
      <div className="companies-mobile-details-rows">
        <div className="companies-mobile-details-row">
          <span>Valor</span>
          <span>{formattedContractValue(contractData)}</span>
        </div>
        <div className="companies-mobile-details-row">
          <span>Índice de reajuste</span>
          <span>
            {readjustmentDataIsLoading ? <Spinner animation="border" size="sm" /> : readjustmentRateLabel}
          </span>
        </div>
        <div className="companies-mobile-details-row">
          <span>Categoria</span>
          <span>{contractTypeLabel}</span>
        </div>
      </div>
      <ContractAttachments contract={contractData} />

      <div className="companies-mobile-details-section-header">
        <p className="companies-mobile-details-title">Produtos</p>
        <span>{`${productsCount} produto${productsCount === 1 ? '' : 's'}`}</span>
      </div>
      <div className="companies-mobile-details-products">
        {company.products.map((product) => (
          <div key={product.name} className="companies-mobile-details-product">
            <Tag type={availabilityTagTypeMap[product.availability]}>{product.acronym}</Tag>
            <span className="companies-mobile-details-product-name">{product.name}</span>
            <span className="companies-mobile-details-product-period">
              {`${formatDate(product.startDateString)} - ${formatDate(product.endDateString)}`}
            </span>
          </div>
        ))}
      </div>

      <div className="companies-mobile-details-section-header">
        <p className="companies-mobile-details-title">Usuários</p>
        <span>{`${users.length} usuário${users.length === 1 ? '' : 's'}`}</span>
      </div>
      <div className="companies-mobile-details-users">
        {users.length === 0 && (
          <p className="companies-mobile-details-empty">Nenhum usuário vinculado.</p>
        )}
        {users.map((user) => (
          <div
            key={user.id}
            className={`companies-mobile-details-user${user.active ? '' : ' inactive'}`}
          >
            <span className="companies-mobile-details-user-name">
              {`${user.first_name} ${user.last_name}`}
            </span>
            <span className="companies-mobile-details-user-username">{user.username}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
